/**
 * SINGLE SOURCE OF TRUTH for the i365 design system (ADR-0007).
 *
 * Mirrored into exactly two places — never copied anywhere else:
 *   - `tailwind.config.js`        → Tailwind utility classes
 *   - `src/styles/themeConfig.ts` → the antd `ConfigProvider` theme
 *
 * Brand palette values are copied **verbatim** from BRAVIX's live site
 * (https://blocatom.com/) — measured from the Elementor global color kit and
 * section backgrounds. Everything else (surfaces, semantic status colors,
 * scales) still follows the HMS-derived system this file started from.
 *
 * A raw hex in a component, `className`, or style prop is a code-review
 * rejection. Import from here (or use a derived Tailwind class) instead.
 */

/* ------------------------------------------------------------------ *
 * Brand palette (verbatim from the BRAVIX site — https://blocatom.com/)
 * ------------------------------------------------------------------ */

export const colors = {
  primary: {
    DEFAULT: '#f15b40',
    hover: '#ca4c36',
    active: '#be280e',
    subtle: '#fef2f0',
  },
  /**
   * Brand navy. `500 → 700 → 900` form the gradient used by the header
   * accents and the mobile navigation drawer. `DEFAULT` matches the site's
   * dark section background and the favicon navy.
   */
  navy: {
    DEFAULT: '#0c3455',
    500: '#385974',
    700: '#0a2b46',
    900: '#071e31',
  },
  green: { DEFAULT: '#36a17d', 200: '#7fe3bd' },
  orange: '#f6a12d',
  /** Unused outside this file (grep-verified) — aligned to `navy.500` rather than left at an orphaned HMS-inherited hex. */
  secondary: '#385974',
  success: { light: '#36a17d', dark: '#7fe3bd' },
  error: { light: '#d1435b', dark: '#f87171' },
  warning: { light: '#f6a12d', dark: '#fbbf24' },
  info: { light: '#0369a1', dark: '#38bdf8' },
  surface: {
    /** `base` is the app background; `container` is a Card/Table surface. */
    light: { base: '#eef5fa', container: '#ffffff', elevated: '#ffffff' },
  },
  text: {
    light: {
      base: '#0b2a3f',
      muted: '#48627a',
      /** True input placeholders only — 2.96 on white, never data. */
      placeholder: '#8299ab',
      onDark: '#eaf4fb',
    },
  },
  border: {
    /** Decorative dividers and card edges only — 1.41 on white. */
    light: '#cfdbe4',
    strong: '#d5e4ef',
  },
} as const;

export const gradients = {
  /**
   * The navy *treatment*: currently only the login hero panel
   * (`BrandHeroPanel.tsx`). The mobile navigation drawer used to carry this
   * gradient too; it was rewritten to the shared light drawer surface (see
   * `app/layout/MobileDrawer.tsx`), so this is no longer a two-consumer
   * token. Tailwind: `bg-brand-navy-gradient`. The flat colour is
   * `bg-brand-navy` (from `colors.navy.DEFAULT`) — the two are separate
   * utilities on purpose.
   */
  brandNavy: 'linear-gradient(155deg, #385974 0%, #0a2b46 48%, #071e31 100%)',
} as const;

/* ------------------------------------------------------------------ *
 * Color contrast contract (ADR-0007 — BINDING)
 * ------------------------------------------------------------------ *
 *
 * AA is the floor: 4.5:1 body text, 3:1 large text and non-text.
 * Every ratio below was measured against WCAG 2.1 relative luminance.
 * Recomputed for the BRAVIX palette (2026-09-23) — numbers below replace the
 * old HMS-blue-primary figures.
 *
 * SAFE — use freely
 *   ink #0b2a3f      on white              14.82  AAA
 *   ink #0b2a3f      on surface #eef5fa    13.46  AAA
 *   ink-muted        on white               6.36  AA
 *   ink-muted        on surface             5.77  AA
 *   navy #0c3455     on white              12.82  AAA
 *   onDark #eaf4fb   on navy #0c3455       11.50  AAA
 *   onDark #eaf4fb   on navy.900 #071e31   15.20  AAA
 *   onDark #eaf4fb   on navy.500 #385974    6.61  AA   (lightest gradient stop —
 *     DOWNGRADED from AAA under the old #0a5580 stop; still clears the AA floor,
 *     do not use for AAA-only contexts)
 *
 * FAILS — never small text on a light surface
 *   warning #f6a12d  on white               2.09  FAIL
 *   success #36a17d  on white               3.21  large text only
 *   primary #f15b40  on white               3.33  large text / non-text (icons,
 *     borders, fills) only — FLAG: this REPLACES the old primary's 5.93 AA
 *     body-text pass. The BRAVIX orange-red is a large-text/non-text color,
 *     not a body-text one; anything currently rendering small `text-primary`
 *     body copy on white must move to `accessibleText.primary` (4.57 on
 *     white) instead. `interactiveBorder.accent` (border use, needs only
 *     3:1) remains valid as-is.
 *   white            on primary #f15b40     3.33  large text / non-text only (see above)
 *   placeholder      on white               2.96  FAIL
 *   border #cfdbe4   on white               1.41  decorative dividers only
 */

/**
 * Text-only in-family darkenings. Use these whenever a semantic color must
 * carry **text or a meaningful icon** on a light surface. The base hues above
 * remain correct for fills, chart series and decoration.
 */
export const accessibleText = {
  /** 4.56 on white. */
  success: '#2c8467',
  /** 4.55 on white. */
  warning: '#a26a1e',
  /** 4.50 on white — muted text that must stay readable. */
  subtle: '#677987',
  /**
   * 4.57 on white — reuses `colors.primary.hover` verbatim, no new hex
   * invented. Added because BRAVIX's primary (3.33 on white) is a
   * large-text/non-text color only; anywhere `primary` needs to carry small
   * body or UI text on a light surface, use this instead.
   */
  primary: '#ca4c36',
} as const;

/**
 * Status fills pair with a FIXED text color. Anything else drops below AA.
 *   success fill + ink   4.62 ✓
 *   warning fill + ink   7.09 ✓
 *   error   fill + white 4.50 ✓   (ink on error is 3.29 — FORBIDDEN)
 */
export const statusFills = {
  success: { bg: colors.success.light, fg: colors.text.light.base },
  warning: { bg: colors.warning.light, fg: colors.text.light.base },
  error: { bg: colors.error.light, fg: '#ffffff' },
  info: { bg: colors.info.light, fg: '#ffffff' },
} as const;

/**
 * Borders that convey state or mark an interactive boundary need ≥3:1.
 * `colors.border.light` (1.41) and `border.strong` (1.30) are decorative only.
 */
export const interactiveBorder = {
  /** 6.36 on white. */
  DEFAULT: colors.text.light.muted,
  /** 3.33 on white — clears the 3:1 non-text floor; do not reuse for text. */
  accent: colors.primary.DEFAULT,
} as const;

/* ------------------------------------------------------------------ *
 * Brand identity (ADR-0015 — RESOLVED, BRAVIX)
 * ------------------------------------------------------------------ *
 *
 * The real brand has landed (D-J is resolved): BRAVIX, an AAC block
 * manufacturer (https://blocatom.com/). The ONE token the identity drops
 * into. `components/ui/BrandMark.tsx` is the ONE component that reads it —
 * nothing else in the app mentions the product name or the mark.
 *
 * Two logo assets now exist, one per background tone (`logo.onLight` /
 * `logo.onDark`); either can still be set back to `null` to fall back to the
 * typographic wordmark (e.g. if a future rebrand drops an asset for only one
 * tone) without touching `BrandMark.tsx`.
 */
export const brand = {
  /** Display name. */
  productName: 'Bravix ERP',
  /** Header / bottom-nav variant where horizontal space is scarce. */
  productNameShort: 'Bravix',
  /** One-line value statement on the login hero. */
  tagline: 'Plant floor to ledger, in one system.',
  /**
   * Real logo asset paths, one per background tone. `null` for a given tone
   * renders the typographic wordmark instead for that tone.
   */
  logo: {
    onLight: '/brand/bravix-wordmark-dark.png' as string | null,
    onDark: '/brand/bravix-wordmark-light.png' as string | null,
  },
} as const;

/* ------------------------------------------------------------------ *
 * Scales
 * ------------------------------------------------------------------ */

export const radius = { sm: 6, md: 8, lg: 12 } as const;

export const spacing = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, '2xl': 32 } as const;

export const typography = {
  fontFamily:
    "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
  fontSize: { xs: 12, sm: 14, md: 16, lg: 18, xl: 22 },
  lineHeight: { tight: 1.25, normal: 1.5 },
  fontWeight: { regular: 400, medium: 500, bold: 700 },
} as const;

/**
 * The Tailwind / antd breakpoint scale. Three systems, one set of numbers.
 * **`md` (768) is the mobile↔desktop boundary** (responsive-design.md).
 */
export const breakpoints = { sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 } as const;

/** Ready-made query strings — components never hand-roll a media query. */
export const mediaQueries = {
  /** The mobile branch. */
  belowMd: `(max-width: ${breakpoints.md - 1}px)`,
  /** The desktop branch. */
  atLeastMd: `(min-width: ${breakpoints.md}px)`,
  atLeastLg: `(min-width: ${breakpoints.lg}px)`,
} as const;

/**
 * WCAG 2.5.5 minimum touch target, in px. Hoisted out of `layout` so other
 * entries in that object can derive from it instead of restating 44 — the
 * navigation row does exactly that.
 */
const TOUCH_TARGET = 44;

export const layout = {
  /**
   * Standalone (unauthenticated / full-page) header height — the login and
   * change-password pages, which render their own plain `<header>` rather than
   * the app shell.
   *
   * Deliberately NOT the shell's header height, and this is why: those pages
   * have no workspace, no navigation and no user menu, so they need a plain
   * band, not the 64px identity row `Layout.headerHeight` now describes. The
   * two numbers differing is intentional — do not "fix" it by collapsing them.
   */
  headerHeight: 56,
  /**
   * The app shell header is **two stacked bars**, mirroring HMS
   * (`templates/BaseLayout.jsx` + `organisms/common/Navbar.jsx`):
   *
   *   row 1 — identity + utilities (`appHeaderIdentityHeight`, HMS `h-16`)
   *   row 2 — navigation only      (`appHeaderNavHeight`)
   *
   * Row 2 renders from **`lg` (1024px)** up; below that the nav lives in the
   * hamburger drawer, so only row 1 renders — `AppShell.tsx`'s
   * `usesDrawerNav = !useIsLargeUp()` is the switch. Both are sticky, so
   * **anything else sticky under the shell must offset by the stack height for
   * its branch** — see `appHeaderStackHeight` / `appHeaderStackHeightBelowLg`,
   * and do not hand-roll the sum.
   *
   * **This said `md` until 2026-10-07 and was wrong.** The real boundary was
   * antd's 992 and is now Tailwind's 1024 (`aefbfba`); the comment named 768
   * throughout. A consumer that trusted it — the sticky table header in
   * `components/table/ResponsiveTable.tsx` — would have offset 108px across
   * the whole 768–1023 tablet band, where only 64px of chrome exists.
   *
   * **Row 2 is 44px, not HMS's 40px, and that 4px is deliberate.** Its only
   * content is the nav links, which are therefore controls, and
   * `responsive-design.md`'s 44px minimum target is binding — ADR-0011 sized
   * the original single header at 56px for exactly this reason. The `md`
   * boundary puts 768–1024px tablets on this desktop branch, so it is a touch
   * row, not a mouse-only one. Expanding the hit area instead (a `tap-44`
   * pseudo-element) was rejected: the overspill lands inside the identity row
   * under the brand mark, and a target that overlaps another control is not a
   * fix. Matching HMS does not extend to breaking our own accessibility floor.
   */
  appHeaderIdentityHeight: 64,
  appHeaderNavHeight: TOUCH_TARGET,
  bottomNavHeight: 56,
  /** WCAG 2.5.5 minimum touch target, in px. */
  touchTarget: TOUCH_TARGET,
  /** Long-form text only, so body copy never runs unbroken on a wide screen. */
  proseMaxWidth: '65ch',
} as const;

/**
 * Combined height of the shell's two sticky header bars **from `lg` (1024px)
 * up** — the offset any sticky toolbar, table header or in-page nav must use
 * on that branch.
 *
 * **Below `lg` the nav row is not rendered at all** (`AppShell.tsx`'s
 * `usesDrawerNav = !useIsLargeUp()`), so the offset there is
 * `appHeaderStackHeightBelowLg`. A consumer whose own branch starts earlier
 * than `lg` — a table header switches to a real table at `md` — must pick
 * between the two on `useIsLargeUp()`; there is no single correct number.
 *
 * Derived, never re-typed: changing a bar's height updates every consumer.
 */
export const appHeaderStackHeight = layout.appHeaderIdentityHeight + layout.appHeaderNavHeight;

/**
 * The same offset **below `lg`**, where only the identity row is sticky.
 *
 * A named companion rather than asking each consumer to reach for
 * `layout.appHeaderIdentityHeight`, so the two branches read as one decision
 * and a grep for `appHeaderStack` finds both.
 */
export const appHeaderStackHeightBelowLg = layout.appHeaderIdentityHeight;

/* ------------------------------------------------------------------ *
 * Notes for the next engineer
 * ------------------------------------------------------------------ *
 *
 * 1. HISTORICAL — resolved 2026-09-23: this note used to flag that ADR-0007's
 *    palette table disagreed with HMS's `designTokens.js` on `navy.900` vs
 *    `navy.DEFAULT`. That HMS-inherited discrepancy no longer applies: the
 *    navy scale (`DEFAULT`/`500`/`700`/`900`) is now sourced verbatim from
 *    the BRAVIX site instead, and all four stops are deliberately distinct —
 *    `DEFAULT` is the flat sider/favicon colour, `500`/`700`/`900` are the
 *    gradient stops. If ADR-0007's table still shows the old HMS hexes, the
 *    ADR needs a follow-up edit to match this file, not the other way round.
 *
 * 2. The `accessibleText` variants were measured on WHITE (4.50–4.56). On the
 *    `surface.light.base` (#eef5fa) app background they fall to 4.09–4.14,
 *    which is below the 4.5 AA floor. Keep semantic text inside a white
 *    container (antd Card/Table use `surface.light.container`), which is where
 *    it lives in practice. Do not place small semantic text directly on the
 *    layout background.
 */
